import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {trackEvent} from 'analytics/Analytics';
import {NucleotideType} from 'biojs/Util';
import {ActionOrigin, MODBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {BulkSetModsParams, PositionalRule} from 'constants/SequenceOpConstants';
import ApplyModPatternModal, {ApplyModPatternModalProps} from 'modbio/bulk-edit-mods/ApplyModPatternModal';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {MissingMonomerReason} from 'molbio/monomers/MonomerConstants';
import NucleotidesEditModal from 'molbio/monomers/NucleotidesEditModal';
import NucleotidesFindAndReplaceModal from 'molbio/monomers/NucleotidesFindAndReplaceModal';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

type MonomerQueryMonomer = MonomerQuery['monomers'][number];

interface Props {
  monomersById: {[id: string]: MonomerQueryMonomer};
  nucleotideType: NucleotideType;
  onBulkUpdateMods: (params: BulkSetModsParams) => void;
  selectedPositions: Set<number>;
  selectedSugarIds: Array<string>;
  selectedBaseIds: Array<string>;
  selectedPhosphateIds: Array<string>;
  sequenceId: string;
  isOligo: boolean;
}

export default function NucleotidesEditMenu(props: Props): JSX.Element {
  const {
    monomersById,
    nucleotideType,
    onBulkUpdateMods,
    selectedPositions,
    selectedSugarIds,
    selectedBaseIds,
    selectedPhosphateIds,
    sequenceId,
    isOligo,
  } = props;

  return (
    <Dropdown
      menu={
        <Menu height={MenuHeight.MEDIUM} width={MenuWidth.MEDIUM} dropLeft={true}>
          <Option
            isDisabled={selectedPositions.size === 0}
            onSelect={() =>
              LegacyModalContainer.open(
                NucleotidesEditModal,
                <NucleotidesEditModal
                  initialSugarId={
                    selectedSugarIds.length === 1 ? selectedSugarIds[0] : MissingMonomerReason.CHOOSING
                  }
                  initialBaseId={
                    selectedBaseIds.length === 1 ? selectedBaseIds[0] : MissingMonomerReason.CHOOSING
                  }
                  initialPhosphateId={
                    selectedPhosphateIds.length === 1
                      ? selectedPhosphateIds[0]
                      : MissingMonomerReason.CHOOSING
                  }
                  monomersById={monomersById}
                  nucleotideType={nucleotideType}
                  onSubmit={(rule: PositionalRule) => {
                    onBulkUpdateMods({rules: [rule]});
                  }}
                  positions={selectedPositions}
                />
              )
            }
            dataTestAttrs={{component: 'NucleotideEditMenu', element: 'option', key: 'editSelection'}}
          >
            Edit selection
          </Option>
          <Option
            onSelect={() => {
              trackEvent(MODBIO_ANALYTICS_ACTIONS.OPENED_FIND_AND_REPLACE_MODAL, {
                actionOrigin: ActionOrigin.MODBIO_NUCLEOTIDES_TAB,
              });
              LegacyModalContainer.open(
                NucleotidesFindAndReplaceModal,
                <NucleotidesFindAndReplaceModal
                  nucleotideType={nucleotideType}
                  sequenceIds={[sequenceId]}
                  shouldSkipResultsScreenAfterSuccess={true}
                  isBulk={false}
                />
              );
            }}
            dataTestAttrs={{component: 'NucleotideEditMenu', element: 'option', key: 'findAndReplace'}}
          >
            Find and replace
          </Option>
          {isOligo && (
            <Option
              onSelect={() => {
                trackEvent(MODBIO_ANALYTICS_ACTIONS.OPENED_MOD_PATTERN_MODAL, {
                  actionOrigin: ActionOrigin.MODBIO_NUCLEOTIDES_TAB,
                });
                legacyRunModal<ApplyModPatternModalProps>(ApplyModPatternModal, {
                  targetOligoIds: [sequenceId],
                  nucleotideType,
                  shouldSkipResultsScreenAfterSuccess: true,
                  isBulk: false,
                });
              }}
              dataTestAttrs={{component: 'NucleotideEditMenu', element: 'option', key: 'applyModPattern'}}
            >
              Apply modification pattern
            </Option>
          )}
        </Menu>
      }
    >
      <Button
        className={css(styles.editButton)}
        isDropdown={true}
        size={ButtonSize.SMALL}
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        dataTestAttrs={{component: 'NucleotideEditMenu', element: 'dropdownButton'}}
      >
        Edit
      </Button>
    </Dropdown>
  );
}

const styles = StyleSheet.create({
  editButton: {
    display: 'flex',
    flexDirection: 'row-reverse',
    width: '56px',
  },
});
